import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';

export default function Chickens() {
  const { isOwner } = useOutletContext();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/api/chickens${showAll ? '?all=1' : ''}`)
      .then(({ batches }) => setData(batches))
      .catch(e => setErr(e.message));
  }, [showAll]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;

  const broilers = data.filter(b => b.type === 'broiler');
  const layers = data.filter(b => b.type === 'layer');

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Chickens</h1>
        <div className="flex items-center gap-3">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Include retired
          </label>
          {isOwner && <Link to="/chickens/new" className="btn-primary">+ Add batch / flock</Link>}
        </div>
      </header>

      <Section title="Broiler batches" emoji="🍗" emptyText="No broiler batches yet.">
        {broilers.map(b => <BatchCard key={b.id} b={b} />)}
      </Section>

      <Section title="Layer flocks" emoji="🥚" emptyText="No layer flocks yet.">
        {layers.map(b => <BatchCard key={b.id} b={b} />)}
      </Section>
    </div>
  );
}

function Section({ title, emoji, emptyText, children }) {
  const arr = Array.isArray(children) ? children : [children];
  const has = arr.filter(Boolean).length > 0;
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-700/70">
        {emoji} {title}
      </h2>
      {has ? <ul className="space-y-2">{children}</ul> : (
        <p className="text-sm text-ink-700/60 italic">{emptyText}</p>
      )}
    </section>
  );
}

function BatchCard({ b }) {
  const startCount = (b.count_in || 0) + b.additions;
  const mortPct = startCount > 0 ? ((b.mortality / startCount) * 100).toFixed(1) : '0.0';
  return (
    <li className="card">
      <Link to={`/chickens/${b.id}`} className="block">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="font-medium text-ink-900">
              {b.name}
              {b.status !== 'active' && <span className="ml-2 pill bg-ink-700/10 text-ink-700">{b.status}</span>}
            </div>
            <div className="text-sm text-ink-700/70 mt-0.5">
              {b.source && <span>{b.source}</span>}
              {b.source_date && <span> · in {fmtDate(b.source_date)}</span>}
              {b.expected_processing_date && <span> · proc {fmtDate(b.expected_processing_date)}</span>}
            </div>
            <div className="text-xs text-ink-700/60 mt-1 flex flex-wrap gap-x-3">
              <span>Start: <strong>{startCount}</strong></span>
              {b.mortality > 0 && <span className="text-rust-700">− {b.mortality} mortality ({mortPct}%)</span>}
              {b.processed > 0 && <span>− {b.processed} processed</span>}
              {b.removed > 0 && <span>− {b.removed} removed</span>}
              <span className="font-semibold text-olive-900">= {b.on_hand} on hand</span>
            </div>
          </div>
          <div className="text-ink-700/40">›</div>
        </div>
      </Link>
    </li>
  );
}
